import React from 'react';
import { Navigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useFeatures } from '../../context/FeatureContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

/**
 * FeatureRoute component that conditionally renders a route based on feature configuration
 * If the feature is disabled, it redirects to the settings page or first available route
 */
const FeatureRoute = ({ 
  feature, 
  children, 
  fallbackPath = '/settings',
  showDisabledMessage = false 
}) => {
  const { isFeatureEnabled, getEnabledFeatures } = useFeatures();

  // If no feature is specified, always show the route
  if (!feature) {
    return children;
  }

  // If feature is enabled, show the route
  if (isFeatureEnabled(feature)) {
    return children;
  }

  // Feature is disabled - determine fallback behavior
  if (showDisabledMessage) {
    return (
      <div className="px-4 py-8 flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <Lock className="h-12 w-12 text-muted-foreground" />
            </div>
            <CardTitle className="text-base">Feature Not Available</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              This feature has been disabled by the system administrator.
            </p>
            <p className="text-sm text-muted-foreground text-center">
              Contact your administrator if you need access to this feature.
            </p>

            <div className="pt-4 border-t">
              <h3 className="text-sm font-semibold mb-3">Available Features:</h3>
              <ul className="space-y-2">
                {getEnabledFeatures().map(featureName => (
                  <li key={featureName} className="text-sm text-muted-foreground capitalize">
                    {featureName}
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Redirect to fallback path
  return <Navigate to={fallbackPath} replace />;
};

export default FeatureRoute;